import { Alert } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { OrganizationType } from '@common/enums/organizationType';
import type { IReduxState } from '../../../../../configureStore';
import type { IAsset } from '../../../../interfaces/asset';
import { AssetCategory } from '../../../../../../common/enums/assetCategory';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { LinkOnClick } from '../../../../elements/common';

interface IWarningToAddOrganizationStampProps {
    careProviderOrganizationId: string | null;
    careProviderPractitionerId: string | null;
    employmentId: string;
    onSaveRequest: (saveType: 'draft') => Promise<string>;
    setHasOrganizationStamp: (value: boolean) => Promise<void> | void;
    validateRequest: (saveType: 'draft') => Promise<boolean>;
}

export function WarningToAddOrganizationStamp(props: IWarningToAddOrganizationStampProps) {
    const navigate = useNavigate();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const organizationType = useSelector((state: IReduxState) => state.authentication.organizationType);
    const [hasOrganizationStamp, setHasOrganizationStamp] = useState(true);
    const [requesterIsGp, setRequesterIsGp] = useState(false);
    const [gpName, setGpName] = useState<string>();

    useEffect(() => {
        loadOrganizationStamp();
        checkIsRequesterGp();
    }, [props.careProviderPractitionerId, props.careProviderOrganizationId]);

    if (hasOrganizationStamp || !props.careProviderPractitionerId) {
        return null;
    }

    return (
        <Alert appearance="warning">
            {requesterIsGp
                ? translateKey('referral.youHaveNoOrganizationStamp') + ' '
                : translateKey('referral.gpHasNoOrganizationStamp', gpName) + ' '}
            {organizationType === OrganizationType.gpPractice ? (
                <LinkOnClick
                    noClassName={true}
                    onClick={async () => await saveRequest()}
                >
                    {translateKey('referral.clickToAddOrganizationStamp')}
                </LinkOnClick>
            ) : (
                translateKey('support.contactServiceDesk')
            )}
        </Alert>
    );

    async function saveRequest() {
        const validatedRequest = await props.validateRequest('draft');
        if (!validatedRequest) {
            return;
        }
        await props.onSaveRequest('draft');

        navigate(`/employments/${props.employmentId}/personalSettings#organizationStamp`);
    }

    async function loadOrganizationStamp() {
        let url;
        if (props.careProviderOrganizationId && props.careProviderPractitionerId) {
            url = `/api/assets?employmentId=${props.employmentId}&alternativeOrganizationId=${props.careProviderOrganizationId}&alternativePractitionerId=${props.careProviderPractitionerId}`;
        } else {
            url = `/api/assets?employmentId=${props.employmentId}`;
        }
        const result = await fetchJson(url, 'GET');
        const organizationStamp = result.json.find(
            (asset: IAsset) => asset.category === AssetCategory.organizationStamp
        );

        if (organizationStamp) {
            setHasOrganizationStamp(true);
            await props.setHasOrganizationStamp(true);
        } else {
            setHasOrganizationStamp(false);
            await props.setHasOrganizationStamp(false);
        }
    }

    async function checkIsRequesterGp() {
        if (
            practitionerId === props.careProviderPractitionerId &&
            organizationId === props.careProviderOrganizationId
        ) {
            setRequesterIsGp(true);
        } else {
            await setNameGeneralPractitioner();
        }
    }

    async function setNameGeneralPractitioner() {
        if (props.careProviderPractitionerId) {
            const result = await fetchJson(`/api/practitioners/${props.careProviderPractitionerId}`, 'GET');
            const name = getDisplayName(result.json);
            setGpName(name);
        }
    }
}
